/**
 * FriendsBadge - Shows how many friends have an item in their lists or watched
 * Used on Discover pages for social integration
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Users } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function FriendsBadge({ itemId, mediaType = 'tv', className = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchFriendsData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.get(
          `${API}/social/friends-with-item/${itemId}?media_type=${mediaType}`,
          { withCredentials: true, headers }
        );
        setData(response.data);
      } catch {
        // Friends badge is non-critical - silent fail is acceptable
      } finally {
        setLoading(false);
      }
    };
    
    fetchFriendsData();
  }, [itemId, mediaType]);
  
  // Don't show anything if loading, no data, or no friends have this item
  if (loading || !data || data.total_friends === 0) {
    return null;
  }
  
  const totalCount = data.in_list_count + data.watched_count;
  const friends = data.friends || [];
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`flex items-center gap-1 ${className}`}>
            {/* Friend avatars (up to 3) */}
            <div className="flex -space-x-2">
              {friends.slice(0, 3).map((friend, idx) => (
                <Avatar key={friend.user_id} className="h-5 w-5 border-2 border-white">
                  {friend.avatar_url || friend.picture ? (
                    <AvatarImage src={friend.avatar_url || friend.picture} />
                  ) : (
                    <AvatarFallback className="text-[8px] bg-[#F87171] text-white">
                      {(friend.display_name || friend.name || '?').charAt(0)}
                    </AvatarFallback>
                  )}
                </Avatar>
              ))}
            </div>
            
            {/* Count badge */}
            <span className="text-xs font-medium text-white bg-[#F87171]/90 px-1.5 py-0.5 rounded-full">
              {data.total_friends}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px]">
          <p className="text-sm">
            <span className="font-medium">{data.total_friends} friend{data.total_friends !== 1 ? 's' : ''}</span>
            {data.watched_count > 0 && data.in_list_count > 0 ? (
              <> ({data.watched_count} watched, {data.in_list_count} on list)</>
            ) : data.watched_count > 0 ? (
              <> watched this</>
            ) : (
              <> have this on their list</>
            )}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default FriendsBadge;
